/**
 * BrokScreen — minimalist centered Brok AI chat view.
 * Single persisted thread; no history switcher.
 *
 * P2b: pulls rueckfrage from useBrokChat and passes it to BrokMessageList.
 *
 * [Source: Story P1b spec §8, Story P2b spec §3.7]
 */
import { MessageSquarePlus } from 'lucide-react';
import { loadBrokerSession } from '../../lib/auth/auth-session';
import { greeting, vornameFromDisplayName, BROK_INPUT_PLACEHOLDER } from './welcome';
import { useBrokChat } from './hooks/useBrokChat';
import { BrokMessageList } from './components/BrokMessageList';
import { SuggestionChips } from './components/SuggestionChips';
import { BrokComposer } from './components/BrokComposer';
import { BrokRay } from './components/BrokRay';

export function BrokScreen() {
  const { messages, streaming, sendMessage, newChat, confirm, reject, rueckfrage } = useBrokChat();

  return (
    <div className="mx-auto flex h-full max-w-[760px] flex-col px-4 py-6">
      {/* Header */}
      <header className="mb-4 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">
            {greeting(vornameFromDisplayName(loadBrokerSession()?.displayName))}
          </h1>
          <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <BrokRay animated={streaming} size={16} label={streaming ? 'Brok arbeitet' : undefined} />
            Brok · dein Assistent
          </p>
        </div>
        {/* Neuer Chat (FIX 2): reset the context so the previous thread + PDF do not bleed in. */}
        <button
          type="button"
          onClick={() => void newChat()}
          disabled={streaming}
          aria-label="Neuer Chat"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-default disabled:opacity-50"
        >
          <MessageSquarePlus className="size-3.5" />
          Neuer Chat
        </button>
      </header>

      {/* Message list (scrollable) */}
      <BrokMessageList
        messages={messages}
        streaming={streaming}
        onConfirm={confirm}
        onReject={(id, note) => void reject(id, note ?? '')}
        onRueckfrage={rueckfrage}
      />

      {/* Suggestion chips + composer */}
      <div className="shrink-0">
        <SuggestionChips onPick={(label) => void sendMessage(label)} disabled={streaming} />
        <BrokComposer
          onSend={(text, attachmentId, attachmentName) =>
            void sendMessage(text, { attachmentId, attachmentName })
          }
          disabled={streaming}
          placeholder={BROK_INPUT_PLACEHOLDER}
        />
      </div>
    </div>
  );
}
